import { Component } from '@angular/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { TranslateService } from '@ngx-translate/core';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DecimalPipe } from '@angular/common';
import * as moment from 'moment';
import { DeviceFactory } from '../../../device-factory/device-factory';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';

@Component({
	selector: 'app-monthly-quarterly-battery-usage-report',
	templateUrl: './monthly-quarterly-battery-usage-report.component.html',
	styleUrls: ['./monthly-quarterly-battery-usage-report.component.css']
})
export class MonthlyQuarterlyBatteryUsageReportComponent {
	siteId: number;
	yearList: any[] = [];
	selectedYear: number = new Date().getFullYear();
	usageData = {};
	siteInstallationDate = null;

	batteryVoltagesOptions: any[] = [];
	selectedBatteryVoltage: string = 'all';

	batteryCapacitiesOptions: any[] = [];
	selectedBatteryCapacity: string = 'all';

	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown;
	selectedBatteryVintage: string[] = ['all'];

	batteryManufacturersOptions: any[] = [];
	selectedBatteryManufacturer: string = 'all';

	periodTypes = [
		{label: this.translate.instant('g.weekly'), id: 'weekly'},
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.quarterly'), id: 'quarterly'},
	];

	selectedPeriodType = 'monthly';

	columnConfig: ColumnsConfig[] = [
		{key: 'period', name: this.translate.instant('connectivity_verification.month'), type: 'string', hasTooltip: null},
		{key: 'batteries_count', name: this.translate.instant('fleet_reports.batteries_count'), type: 'number'},
		{key: 'used_batteries_count', name: this.translate.instant('fleet_reports.used_batteries_count'), type: 'number', hasTooltip: this.translate.instant('tooltip.battlinkDevice.site_monthly_used_devices_count_column')},
		{key: 'usage_score', name: this.translate.instant('fleet_reports.utilization_score_percent'), type: 'number', hasTooltip: this.translate.instant('tooltip.battlinkDevice.site_monthly_usage_utilization_score_column')},
		{key: 'average_inuse_ah', name: this.translate.instant('fleet_reports.average_daily_inuse_ah'), type: 'number'},
		{key: 'energy_usage_score', name: this.translate.instant('fleet_reports.energy_usage_score'), type: 'number', hasTooltip: this.translate.instant('tooltip.battlinkDevice.site_monthly_energy_utilization_score_column')},
		{key: 'average_charge_ah', name: this.translate.instant('fleet_reports.average_daily_charge_ah'), type: 'number'},
		{key: 'average_inuse_hours', name: this.translate.instant('fleet_reports.average_daily_inuse_hours'), type: 'duration'},
		{key: 'average_charge_hours', name: this.translate.instant('fleet_reports.average_daily_charge_hours'), type: 'duration'},
		{key: 'average_idle_hours', name: this.translate.instant('fleet_reports.average_daily_idle_hours'), type: 'duration'},
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('fleet_reports.monthly_quarterly_battery_usage_report'),
		csvFileHeaderInfo: []
	};

	allYearsId = 9999;

	constructor(
		private sitesService: SitesService,
		private translate: TranslateService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
	) {}

	ngOnInit() {
		this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.siteId = siteInfo.id;
				this.getMonthlyQuarterlyBatteryUsageReport();
				const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
				this.yearList = yearList;
				this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
				this.selectedYear = currentYear;

				this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
					customerName: siteInfo.customer_name,
					siteName: siteInfo.name,
					includeSpacing: false
				});
			}
		});

	}

	getMonthlyQuarterlyBatteryUsageReport() {
		const options = {
			batteryCapacity: this.selectedBatteryCapacity,
			batteryVoltage: this.selectedBatteryVoltage,
			batteryVintage: this.selectedBatteryVintage,
			batteryManufacturer: this.selectedBatteryManufacturer,
			getAllYears: this.isAllYearsSelected(),
		};

		const battlinkTypes = [DeviceFactory.types.battlink, DeviceFactory.types.lithiumBattlink, DeviceFactory.types.battlinkII];
		this.sitesService.getMonthlyQuarterlyUsageReport(this.siteId, this.selectedYear, battlinkTypes, options).subscribe((res: any) => {
			if (!res.quarterly && !res.monthly)
				return;

			this.usageData = res;
			this.batteryVoltagesOptions = this.commonService.prepareOptions(res.batteryVoltages);
			this.batteryCapacitiesOptions = this.commonService.prepareOptions(res.batteryCapacities);
			this.batteryManufacturersOptions = this.commonService.prepareOptions(res.batteryManufacturers);

			const data = this.isAllYearsSelected() ? res?.yearly || {} || {} : res?.[this.selectedPeriodType]?.[this.selectedYear] || {};
			this.updateTableData(data);
		});
	}

	selectYear() {
		this.columnConfig[0].name = this.getPeriodName();
		this.getMonthlyQuarterlyBatteryUsageReport();
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	changePeriodType() {
		this.columnConfig[0].name = this.getPeriodName();

		this.columnConfig[0].hasTooltip = null;
		if (this.selectedPeriodType === 'weekly')
			this.columnConfig[0].hasTooltip = this.translate.instant('tooltip.battlinkDevice.weekly_period');

		this.updateTableData(this.usageData?.[this.selectedPeriodType]?.[this.selectedYear] || []);
	}

	getPeriodName() {
		if (this.isAllYearsSelected())
			return this.translate.instant('g.year');

		switch (this.selectedPeriodType) {
			case 'monthly':
				return this.translate.instant('connectivity_verification.month');
			case 'quarterly':
				return this.translate.instant('site.quarter');
			case 'weekly':
				return this.translate.instant('g.week');
			default:
				return '';
		}
	}

	getPeriodValue(data, period) {
		if (this.isAllYearsSelected())
			return data[period].year;

		let value = '';
		switch (this.selectedPeriodType) {
			case 'monthly':
				value = this.commonService.monthsText[data[period].month - 1];
				break;
			case 'quarterly':
				value = data[period].quarter;
				break;
			case 'weekly':
				value = `${data[period].week} (${data[period].week_range.start} - ${data[period].week_range.end})`;
				break;
			default:
				value = '';
		}

		return value;
	}

	updateTableData(data) {
		const tableData = [];
		const decimalPipe: DecimalPipe = new DecimalPipe("en-US");

		for (const period in data) {
			if (data.hasOwnProperty(period)) {
				const averageInuseAs = this.findAverage(data[period].total_inuse_as, data[period].daysCount);
				const averageChargeAs = this.findAverage(data[period].total_charge_as, data[period].daysCount);
				const averageInuseSecond = this.findAverage(data[period].total_inuse_second, data[period].daysCount);
				const averageChargeSecond = this.findAverage(data[period].total_charge_second, data[period].daysCount);
				const averageIdleSecond = this.findAverage(data[period].total_idle_second, data[period].daysCount);

				tableData.push({
				 	month: data[period].month,
					period: {value: this.getPeriodValue(data, period)},
					batteries_count: {value: data[period].batteries_count},
					used_batteries_count: {value: data[period].used_batteries_count},
					usage_score: {value: this.siteDashboardService.calculateUsageScore(data[period].batteries_count, data[period].used_batteries_count)},
					average_inuse_ah: {value: decimalPipe.transform(averageInuseAs / 3600 || 0, '1.0-1')},
					energy_usage_score: {value: +(data[period].energy_usage_score).toFixed(1)},
					average_charge_ah: {value: decimalPipe.transform(averageChargeAs / 3600 || 0, '1.0-1')},
					average_inuse_hours: {value: this.commonService.longTimeFormat(averageInuseSecond || 0), sortValue: averageInuseSecond || 0},
					average_charge_hours: {value: this.commonService.longTimeFormat(averageChargeSecond || 0), sortValue: averageChargeSecond || 0},
					average_idle_hours: {value: this.commonService.longTimeFormat(averageIdleSecond || 0), sortValue: averageIdleSecond || 0},
				});
			}
		}

		// sort by period
		tableData.sort((a, b) => { return a.month - b.month; });
		this.tableData = tableData;
	}

	findAverage(data: number, daysCount: number) {
		if (!data || !daysCount)
			return 0;

		return data / daysCount;
	}

	batteryVintageSelectionChanged() {
		if (!this.selectedBatteryVintage.length)
			this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.selectedBatteryVintage.length > 1 && this.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedBatteryVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedBatteryVintage = this.selectedBatteryVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.getMonthlyQuarterlyBatteryUsageReport();
	}
}
